import { useMutation, useQueryClient } from '@tanstack/react-query';
import { warehouseService } from '@services/warehouse';
import {
  EquipmentAsset,
  EquipmentAssetCreateInput,
  EquipmentAssetUpdateInput,
  EquipmentCheckoutInput,
  EquipmentReturnInput,
  EquipmentMaintenanceInput,
  EquipmentReserveInput,
  EquipmentUpdateReservationInput,
} from '@models/warehouse.model';
import { EQUIPMENT_ASSETS_QUERY_KEY } from './useEquipmentAssets';
import { EQUIPMENT_RESERVATIONS_QUERY_KEY } from './useEquipmentReservations';

export const useCreateEquipmentAsset = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentAssetCreateInput>({
    mutationFn: (input) => warehouseService.createEquipmentAsset(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
  });
};

export const useUpdateEquipmentAsset = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentAssetUpdateInput>({
    mutationFn: (input) => warehouseService.updateEquipmentAsset(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.setQueryData<EquipmentAsset>(
        [...EQUIPMENT_ASSETS_QUERY_KEY, data.assetId],
        data
      );
    },
  });
};

export const useCheckoutEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentCheckoutInput>({
    mutationFn: (input) => warehouseService.checkoutEquipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useReturnEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentReturnInput>({
    mutationFn: (input) => warehouseService.returnEquipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useReserveEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentReserveInput>({
    mutationFn: (input) => warehouseService.reserveEquipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useSetEquipmentMaintenance = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentMaintenanceInput>({
    mutationFn: (input) => warehouseService.setEquipmentMaintenance(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useCompleteEquipmentMaintenance = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, string>({
    mutationFn: (assetId) => warehouseService.completeEquipmentMaintenance(assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useUpdateEquipmentReservation = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, EquipmentUpdateReservationInput>({
    mutationFn: (input) => warehouseService.updateEquipmentReservation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useCancelEquipmentReservation = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentAsset, Error, string>({
    mutationFn: (reservationId) => warehouseService.cancelEquipmentReservation(reservationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_RESERVATIONS_QUERY_KEY });
    },
  });
};

export const useDeleteEquipmentAsset = () => {
  const queryClient = useQueryClient();

  return useMutation<{ affected: number }, Error, string>({
    mutationFn: (assetId) => warehouseService.deleteEquipmentAsset(assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
  });
};
